import { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Vehicle, FilterState } from '../types';
import { DEFAULT_FILTERS, PROGRESS_STATUSES } from '../types';
import { vehiclesApi } from '../api/vehicles';
import { dealershipsApi } from '../api/dealerships';
import { tagsApi } from '../api/tags';
import VehicleCard from '../components/VehicleCard';
import VehicleModal from '../components/VehicleModal';
import FilterPanel from '../components/FilterPanel';
import ActiveFilters from '../components/ActiveFilters';
import BulkActionBar from '../components/BulkActionBar';

function filtersToParams(f: FilterState): Record<string, string> {
  const p: Record<string, string> = {};
  if (f.search)              p.search = f.search;
  if (f.vehicle_class.length) p.vehicle_class = f.vehicle_class.join(',');
  if (f.category.length)     p.category = f.category.join(',');
  if (f.tier.length)         p.tier = f.tier.join(',');
  if (f.progress.length)     p.progress = f.progress.join(',');
  if (f.dealership_id)       p.dealership_id = f.dealership_id;
  if (f.dealership_available) p.dealership_available = '1';
  if (f.boosting_exclusive)  p.boosting_exclusive = '1';
  if (f.rp_exclusive)        p.rp_exclusive = '1';
  if (f.min_doors)           p.min_doors = f.min_doors;
  if (f.max_doors)           p.max_doors = f.max_doors;
  if (f.min_seats)           p.min_seats = f.min_seats;
  if (f.max_seats)           p.max_seats = f.max_seats;
  if (f.min_speed)           p.min_speed = f.min_speed;
  if (f.max_speed)           p.max_speed = f.max_speed;
  if (f.tag_ids.length)      p.tag_ids = f.tag_ids.join(',');
  return p;
}

function paramsToFilters(sp: URLSearchParams): FilterState {
  return {
    search: sp.get('search') ?? '',
    vehicle_class: sp.get('vehicle_class')?.split(',').filter(Boolean) ?? [],
    category: sp.get('category')?.split(',').filter(Boolean) ?? [],
    tier: sp.get('tier')?.split(',').filter(Boolean) ?? [],
    progress: sp.get('progress')?.split(',').filter(Boolean) ?? [],
    dealership_id: sp.get('dealership_id') ?? '',
    dealership_available: sp.get('dealership_available') === '1',
    boosting_exclusive: sp.get('boosting_exclusive') === '1',
    rp_exclusive: sp.get('rp_exclusive') === '1',
    min_doors: sp.get('min_doors') ?? '',
    max_doors: sp.get('max_doors') ?? '',
    min_seats: sp.get('min_seats') ?? '',
    max_seats: sp.get('max_seats') ?? '',
    min_speed: sp.get('min_speed') ?? '',
    max_speed: sp.get('max_speed') ?? '',
    tag_ids: sp.get('tag_ids')?.split(',').filter(Boolean) ?? [],
  };
}

export default function VehicleGrid() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(() => paramsToFilters(searchParams));
  const [filterOpen, setFilterOpen] = useState(false);
  const [modal, setModal] = useState<{ vehicle: Vehicle | null; open: boolean }>({ vehicle: null, open: false });
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const gridRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const { data: vehiclesData, isLoading } = useQuery({
    queryKey: ['vehicles', filters],
    queryFn: () => vehiclesApi.list(filters),
  });

  const { data: dealershipsData } = useQuery({
    queryKey: ['dealerships'],
    queryFn: () => dealershipsApi.list(),
  });

  const { data: tagsData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
  });

  // Stagger cards in on data load
  useGSAP(() => {
    if (!isLoading && gridRef.current) {
      const cards = gridRef.current.querySelectorAll('.vehicle-card');
      gsap.from(cards, {
        y: 16, opacity: 0,
        stagger: { amount: 0.4, from: 'start' },
        duration: 0.35,
        ease: 'power3.out',
      });
    }
  }, [isLoading, vehiclesData]);

  // Sync filters → URL
  useEffect(() => {
    setSearchParams(filtersToParams(filters), { replace: true });
  }, [filters]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') { e.preventDefault(); searchRef.current?.focus(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') { e.preventDefault(); setModal({ vehicle: null, open: true }); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const updateFilters = useCallback((patch: Partial<FilterState>) => {
    setFilters(f => ({ ...f, ...patch }));
  }, []);

  const clearFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const bulkPatchMutation = useMutation({
    mutationFn: async (patch: Partial<Vehicle> & { tag_ids?: number[] }) => {
      await Promise.all([...selected].map(id => vehiclesApi.patch(id, patch)));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  });

  const bulkTagAddMutation = useMutation({
    mutationFn: async (tagId: number) => {
      await Promise.all([...selected].map(id => {
        const v = vehicles.find(v => v.id === id);
        const existing = v?.tags?.map(t => t.id) ?? [];
        if (existing.includes(tagId)) return Promise.resolve();
        return vehiclesApi.patch(id, { tag_ids: [...existing, tagId] });
      }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  });

  const bulkTagRemoveMutation = useMutation({
    mutationFn: async (tagId: number) => {
      await Promise.all([...selected].map(id => {
        const v = vehicles.find(v => v.id === id);
        const existing = v?.tags?.map(t => t.id) ?? [];
        return vehiclesApi.patch(id, { tag_ids: existing.filter(i => i !== tagId) });
      }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      await Promise.all([...selected].map(id => vehiclesApi.delete(id)));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      setSelected(new Set());
      setBulkMode(false);
    },
  });

  const bulkIsPending =
    bulkPatchMutation.isPending ||
    bulkTagAddMutation.isPending ||
    bulkTagRemoveMutation.isPending ||
    bulkDeleteMutation.isPending;

  const vehicles = vehiclesData?.vehicles ?? [];
  const dealerships = dealershipsData?.dealerships ?? [];
  const tags = tagsData?.tags ?? [];

  // Summary stats
  const stats = PROGRESS_STATUSES.map(s => ({
    label: s.replace('In Progress / Active Editing', 'In Progress'),
    count: vehicles.filter(v => v.progress === s).length,
  }));
  const noImage = vehicles.filter(v => !v.image_key && !v.image_url).length;

  const exportCsv = async () => {
    const res = await vehiclesApi.exportCsv();
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'sakura-vehicles.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen sakura-bg">
      {/* Toolbar */}
      <div className="border-b px-4 py-3 flex items-center gap-3 flex-wrap sticky top-0 z-30" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
        <input
          ref={searchRef}
          className="input flex-1 min-w-48 max-w-72"
          placeholder="Search name or spawncode… (Ctrl+F)"
          value={filters.search}
          onChange={e => updateFilters({ search: e.target.value })}
        />

        <button
          onClick={() => setFilterOpen(v => !v)}
          className={`px-3 py-2 border text-xs font-heading uppercase transition-colors ${filterOpen ? 'bg-dark text-white border-dark' : ''}`}
          style={!filterOpen ? { borderColor: 'var(--border-strong)', color: 'var(--text)' } : undefined}
        >
          Filters {filterOpen ? '▲' : '▼'}
        </button>

        <button
          onClick={() => { setBulkMode(v => !v); setSelected(new Set()); }}
          className={`px-3 py-2 border text-xs font-heading uppercase transition-colors ${bulkMode ? 'bg-primary text-white border-primary' : ''}`}
          style={!bulkMode ? { borderColor: 'var(--border-strong)', color: 'var(--text)' } : undefined}
        >
          {bulkMode ? `Bulk (${selected.size})` : 'Bulk Edit'}
        </button>

        <div className="ml-auto flex gap-2">
          <button onClick={exportCsv} className="px-3 py-2 border text-xs font-heading uppercase transition-colors" style={{ borderColor: 'var(--border-strong)', color: 'var(--text)' }}>
            Export CSV
          </button>
          <button
            onClick={() => setModal({ vehicle: null, open: true })}
            className="px-4 py-2 bg-dark text-white text-xs font-heading uppercase hover:bg-primary transition-colors"
          >
            + Add Vehicle
          </button>
        </div>
      </div>

      {/* Filter panel */}
      <FilterPanel
        filters={filters}
        onChange={updateFilters}
        onClear={clearFilters}
        open={filterOpen}
        dealerships={dealerships}
        tags={tags}
      />

      {/* Active filter pills */}
      <ActiveFilters
        filters={filters}
        onChange={updateFilters}
        onClear={clearFilters}
        tags={tags}
        dealerships={dealerships}
      />

      {/* Stats bar */}
      <div className="border-b px-4 py-2 flex items-center gap-4 text-xs font-body flex-wrap" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <span className="font-heading" style={{ color: 'var(--text)' }}>{vehicles.length} vehicles</span>
        <span style={{ color: 'var(--border-strong)' }}>|</span>
        {stats.map(s => (
          <span key={s.label} style={{ color: 'var(--text-muted)' }}>
            <span className="font-heading" style={{ color: 'var(--text)' }}>{s.count}</span> {s.label}
          </span>
        ))}
        <span style={{ color: 'var(--border-strong)' }}>|</span>
        <span style={{ color: 'var(--text-muted)' }}><span className="font-heading text-primary">{noImage}</span> missing images</span>
      </div>

      {/* Grid — bottom padding so content clears the bulk bar */}
      <div className={`p-4 ${bulkMode && selected.size > 0 ? 'pb-40' : ''}`}>
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
            {Array.from({ length: 16 }).map((_, i) => (
              <div key={i} className="h-52 animate-pulse" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} />
            ))}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <p className="font-heading text-2xl tracking-wide" style={{ color: 'var(--text-faint)' }}>No vehicles found</p>
            <p className="font-body text-sm mt-2" style={{ color: 'var(--text-muted)' }}>Try adjusting your filters.</p>
          </div>
        ) : (
          <div ref={gridRef} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
            {vehicles.map(v => (
              <VehicleCard
                key={v.id}
                vehicle={v}
                onClick={(vehicle) => setModal({ vehicle, open: true })}
                selected={selected.has(v.id)}
                selectable={bulkMode}
                onSelect={(id) => setSelected(s => {
                  const ns = new Set(s);
                  ns.has(id) ? ns.delete(id) : ns.add(id);
                  return ns;
                })}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {bulkMode && selected.size > 0 && (
        <BulkActionBar
          selected={selected}
          vehicles={vehicles}
          tags={tags}
          dealerships={dealerships}
          isPending={bulkIsPending}
          onSelectAll={() => setSelected(new Set(vehicles.map(v => v.id)))}
          onClear={() => setSelected(new Set())}
          onBulkPatch={patch => bulkPatchMutation.mutate(patch)}
          onBulkTagAdd={tagId => bulkTagAddMutation.mutate(tagId)}
          onBulkTagRemove={tagId => bulkTagRemoveMutation.mutate(tagId)}
          onBulkDelete={() => bulkDeleteMutation.mutate()}
        />
      )}

      {/* Modal */}
      {modal.open && (
        <VehicleModal
          vehicle={modal.vehicle}
          onClose={() => setModal({ vehicle: null, open: false })}
          tags={tags}
          dealerships={dealerships}
        />
      )}
    </div>
  );
}
